import React, { useCallback, useMemo, useLayoutEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { blocks, globals, view } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import getParentBlockByElement from 'src/js/modules/blocks/helpers/get-parent-block-by-element';
import highlighter from '../../highlighter';
import ContextMenuItem from '../components/context-menu-item';
import isTouchSupport from '../verifiers/is-touch-support';

const {
  trackers: { trackEvent },
} = segment;

function ContextMenuGroupContainer({ group }) {
  const { element } = group;
  const isTouchDevice = isTouchSupport();
  const prevHighlightedElements = useMemo(() => highlighter.accessors.getHighlightedElements(), []);
  const focusedElementId = useSelector(view.selectors.getFocusedElement);
  const selectedElementId = useSelector(view.selectors.getSelectedElement);

  const [isLoading, setIsLoading] = useState(true);
  const disabledStatesMap = useRef({});

  const onMouseEnterHandler = useCallback(() => {
    const { onMouseEnter } = group;

    let shouldPreventDefault = false;

    const preventDefaultHandler = () => {
      shouldPreventDefault = true;
    };

    if (typeof onMouseEnter === 'function') {
      onMouseEnter({
        preventDefaultHandler,
      });
    }

    if (shouldPreventDefault || isTouchDevice || !element) return;

    highlighter.operations.show([element]);

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [element, isTouchDevice]);

  const onMouseLeaveHandler = useCallback(() => {
    const { onMouseLeave } = group;

    let shouldPreventDefault = false;

    const preventDefaultHandler = () => {
      shouldPreventDefault = true;
    };

    if (typeof onMouseLeave === 'function') {
      onMouseLeave({
        preventDefaultHandler,
      });
    }

    if (shouldPreventDefault || isTouchDevice) return;

    if (!element || (!focusedElementId && !selectedElementId)) {
      highlighter.operations.hide();
      return;
    }

    const wasElementHighlightedBefore = prevHighlightedElements.some(
      ([prevElement]) => prevElement === element
    );

    if (!wasElementHighlightedBefore) {
      highlighter.operations.hide([element]);
    }

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [isTouchDevice, element, focusedElementId, prevHighlightedElements, selectedElementId]);

  const onMouseDownHandler = useCallback(() => {
    const { onMouseDown } = group;

    if (typeof onMouseDown === 'function') {
      onMouseDown();
    }
  }, [group]);

  const onBeforeItemClickHandler = ({ id: itemId, element: currentElement }) => {
    const { onBeforeItemClick } = group;

    let shouldPreventDefault = false;

    const preventDefaultHandler = () => {
      shouldPreventDefault = true;
    };

    if (typeof onBeforeItemClick === 'function') {
      onBeforeItemClick({
        preventDefaultHandler,
      });
    }

    if (shouldPreventDefault) return;

    const parentBlock = getParentBlockByElement(currentElement);

    const isElementGlobal = globals.verifiers.isElementGlobal(
      view.accessors.getLiveElementId(currentElement)
    );
    const targetElementType = isElementGlobal ? 'global' : 'element';
    const wasGlobalOriginallyDetouched = isElementGlobal
      ? globals.verifiers.isElementDetached(currentElement)
      : null;

    trackEvent(segment.constants.events.CONTEXT_MENU_ITEM_CLICKED, {
      itemId,
      blockId: blocks.accessors.getBlockIdByElement(parentBlock),
      blockVariationId: blocks.accessors.getVariationIdByElement(parentBlock),
      targetElementType,
      wasGlobalOriginallyDetouched,
    });

    highlighter.operations.hide();
  };

  useLayoutEffect(() => {
    const loadItems = async () => {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of group.items) {
        if (item.disabled) {
          // eslint-disable-next-line no-await-in-loop
          disabledStatesMap.current[item.id] = await item.disabled();
        }
      }

      setIsLoading(false);
    };

    loadItems();
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      {group.items.map((item) => {
        if (item.menuNode) {
          return (
            <React.Fragment key={item.id + item.caption}>
              {React.cloneElement(item.menuNode, { element })}
            </React.Fragment>
          );
        }

        return (
          <ContextMenuItem
            key={item.id + item.caption}
            id={item.id}
            element={group.element}
            caption={item.caption}
            glyph={item.glyph}
            contentRight={item.contentRight}
            disabled={isLoading || disabledStatesMap.current[item.id]}
            onClick={item.onClick}
            active={item.active}
            iconPlacement={item.iconPlacement}
            iconType={item.iconType}
            withBetaLabel={item.withBetaLabel}
            onMouseEnter={onMouseEnterHandler}
            onMouseLeave={onMouseLeaveHandler}
            onMouseDown={onMouseDownHandler}
            beforeItemClick={onBeforeItemClickHandler}
          />
        );
      })}
    </React.Fragment>
  );
}

ContextMenuGroupContainer.propTypes = {
  group: PropTypes.shape({
    name: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        caption: PropTypes.string,
        glyph: PropTypes.string,
        order: PropTypes.number,
        iconPlacement: PropTypes.string,
        iconType: PropTypes.string,
        withBetaLabel: PropTypes.bool,
        disabled: PropTypes.func,
        onClick: PropTypes.func,
        matches: PropTypes.func,
      })
    ),
    element: PropTypes.object,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onMouseDown: PropTypes.func,
    onBeforeItemClick: PropTypes.func,
  }).isRequired,
};

export default ContextMenuGroupContainer;
